import { useState } from "react";
import { useWallet } from "../../../../hooks/useWallet";
import { useSimlifyPayment } from "../../../../hooks/useSimlifyPayment";
import { ApiCall, GetApiCall } from "../../../../helper/axios";
import { toUrlVendor } from "../../../../helper/vendorMask";
import moment from "moment";
import {
  addServiceFees,
  formatPrice,
  getUserToken,
  paymentCancelCommonFn,
  paymentCaptureCommonFn
} from "../../../../helper/commonFunction";
import StripeWrapper from "../../../../component/StripeWrapper";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const preparePayload = (data, hotelDetails , paymentmethod = "wallet" , onineServicesFees,transcationId,basePrice,agentActiveCurrency) => {
  let guestName = "";
  const rawAmount = hotelDetails?.amount;
  const expectedAmount = parseFloat(rawAmount) || 0;

  let payload = {
    checkinDate: moment(hotelDetails?.checkInDate).format("YYYY-MM-DD"),
    checkoutDate: moment(hotelDetails?.checkOutDate).format("YYYY-MM-DD"),
    cityId: hotelDetails?.cityId,
    nationalityId: hotelDetails?.nationalityId,
    hotelCode: hotelDetails?.id,
    guestName: guestName,
    hotelName: hotelDetails?.name,
    expectedAmount: expectedAmount,
    clientRefNo: hotelDetails?.partnerId,
    _id: hotelDetails?._id,
    contactDetails: data?.contactDetails,
    paymentmethod,
    transcationId,
    vendor : hotelDetails?.vendor,
    activeCurrency : agentActiveCurrency
  };

  if(hotelDetails?.vendor === 'HOTELRACK'){
    payload = {
      ...payload,
      clientRefNo : hotelDetails?._id,
      roomInfo: data.roomInfoValues.map((r, roomIndex) => {
        const adults =
          r?.adults?.map((p) => {
            if (!guestName) {
              guestName = p?.firstName + " " + p?.lastName;
            }
            return {
              isChild: false,
              title: p?.gender === "Male" ? "Mr." : "Mrs.",
              firstName: p?.firstName,
              lastName: p?.lastName,
              passportNo: p?.passportNo ?? "",
            };
          }) ?? [];
  
        const childs =
          r?.childs?.map((p, index) => {
            return {
              isChild: true,
              title: p?.gender === "Master" ? "Master" : "Miss",
              firstName: p?.firstName,
              lastName: p?.lastName,
              passportNo: p?.passportNo ?? "",
              childAge:
                hotelDetails?.roomDetail?.[roomIndex].childAges?.[index] ?? 0,
            };
          }) ?? [];
  
        return {
          noOfAdult: r?.adults?.length,
          noOfChild: r?.childs?.length,
          childAges: hotelDetails?.roomDetail?.[roomIndex].childAges,
          roomSrNo: r?.roomInfo?.roomSrNo,
          rateKey: r?.roomInfo?.rateKey,
          paxInfo: [...adults, ...childs],
        };
      }),
      tokenId: hotelDetails?.apiResponse?.tokenId,
      hotelKey: hotelDetails?.apiResponse?.hotelKey,
      voucherBooking: true,
      expectedAmount: parseFloat(hotelDetails?.apiResponse?.amount ?? hotelDetails?.amount) || 0,
    }
  } else if (hotelDetails?.vendor === 'RATEHAWK' || hotelDetails?.vendor === 'DAHRTOURS'){
    payload = {
      ...payload,
      hash : hotelDetails?.apiResponse?.book_hash,
      roomInfo: data.roomInfoValues.map((r, roomIndex) => {
        const adults =
          r?.adults?.map((p) => {
            if (!guestName) {
              guestName = p?.firstName + " " + p?.lastName;
            }
            return {
              isChild: false,
              title: p?.gender === "Male" ? "Mr." : "Mrs.",
              firstName: p?.firstName,
              lastName: p?.lastName,
              passportNo: p?.passportNo ?? "",
            };
          }) ?? [];
  
        const childs =
          r?.childs?.map((p, index) => {
            return {
              isChild: true,
              title: p?.gender === "Master" ? "Master" : "Miss",
              firstName: p?.firstName,
              lastName: p?.lastName,
              passportNo: p?.passportNo ?? "",
              childAge:
                hotelDetails?.roomDetail?.[roomIndex].childAges?.[index] ?? 0,
            };
          }) ?? [];
  
        return {
          noOfAdult: r?.adults?.length,
          noOfChild: r?.childs?.length,
          childAges: hotelDetails?.roomDetail?.[roomIndex].childAges,
          paxInfo: [...adults, ...childs],
        };
      }),
    }
  }

  if (hotelDetails?.apiResponse?.packageRate) {
    payload = {
      ...payload,
      AirlineName: "AirIndia",
      AirlinePNR: "12356789",
    }
  }

  if(paymentmethod === 'online'){
    let serviceFeeAddedAmount = (parseFloat(basePrice) * parseFloat(onineServicesFees) / 100)
    payload = {...payload , onlineServiceFees : parseFloat(serviceFeeAddedAmount) || 0}
  }

  if (data?.bookingReferenceNote) {
    payload.bookingReferenceNote = data?.bookingReferenceNote ?? "";
  }

  return payload;
};

export const useBookingConfirm = (totalPrice, refresh, basePrice = null,onineServicesFees=null) => {
  const navigate = useNavigate()
  const { agentActiveCurrency } = useSelector((state) => state.userData);
  const userToken = getUserToken();
  const {
    debit,
    credit,
    getBalance,
    refreshWalletBalance,
    loading: walletLoading,
    error: walletPaymentError,
  } = useWallet();

  const confirmBooking = async (payload, loyaltyAmount = null) => {
    const response = await ApiCall("POST", "/hotels/confirm-booking", payload);
    if (response?.data?.data?.bookingReference && (response?.data?.data?.bookingStatus === 'RR' || response?.data?.data?.bookingStatus === 'CONFIRMED')) {
      const amountToUse = loyaltyAmount || totalPrice;
      let nonceToken = await generateNonceForTranscationFn('loyalty', 'credit');
      ApiCall('POST', `wp-json/dahr/v1/loyalty-points-transaction`, {
        userToken: userToken,
        amount: parseFloat(amountToUse) || 0,
        booking_type: "vendor_hotel_booking",
        booking_id: response?.data?.data?.bookingReference,
        transaction_comment: `Credited Towards: Hotel Booking`,
        nonce: nonceToken,
      }, {}, 'wordpress');
      await refreshWalletBalance();
      if(payload?.vendor === 'RATEHAWK' || payload?.vendor === 'DAHRTOURS'){
        navigate(`/hotels/checkout/${response?.data?.data?.bookingReference}?bookingId=${response?.data?.data?.bookingId}&type=${toUrlVendor(payload?.vendor)}`)
        return response
      }
      navigate(`/hotels/checkout/${payload?.clientRefNo}?bookingId=${response?.data?.data?.bookingReference}&type=${toUrlVendor(payload?.vendor)}`)
    }
    return response;
  };

  const {
    payloadRef,
    loading: onlinePaymentLoading,
    error: onlinePaymentError,
    addListner,
  } = useSimlifyPayment(parseFloat(totalPrice) || 0, confirmBooking, () => {
    return basePrice ? parseFloat(basePrice) || 0 : null;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingError,setbookingError]=useState("")

  const generateNonceForTranscationFn = async (transcationType = 'wallet', type = 'debit') => {
    let nonceToken = '';
    const nonceData = await GetApiCall('GET', `wp-json/dahr/v1/generate-nonce?userToken=${userToken}&transaction_type=${type}&type=${transcationType}`, {}, 'wordpress');
    if (nonceData?.status === 200) {
      nonceToken = nonceData?.data?.nonce;
    }
    return nonceToken;
  }

  const confirmAfterOnlinePayment = async (payload, hotelDetails, onineServicesFees = 0, paymentMethod = {} , totalPriceForOnline,basePrice=null,setMode) => {
    let transactionId;
    try {
      setLoading(true);
      setError(null);
      if(paymentMethod?.status === 'requires_capture'){
        const deductBalanceFromWallet = await debit(
            totalPriceForOnline,
            "Debited Towards: Hotel Booking",
            "online"
        );
        if (
            deductBalanceFromWallet?.status === 200 &&
            deductBalanceFromWallet?.data?.success &&
            deductBalanceFromWallet?.data?.transaction_id
        ){
          transactionId = deductBalanceFromWallet?.data?.transaction_id;
          const reqPayload = preparePayload(payload, hotelDetails, "online", onineServicesFees, paymentMethod?.id,basePrice,agentActiveCurrency);
          payloadRef.current = reqPayload;
          const confirmResponse = await confirmBooking(reqPayload);
          if (confirmResponse?.data?.data?.bookingReference && confirmResponse?.data?.data?.bookingStatus === 'RR' || confirmResponse?.data?.data?.bookingStatus === 'CONFIRMED') {
            // await paymentCaptureCommonFn(paymentMethod?.id,userToken);
            setbookingError("");
            window.scrollTo(0, 0);
            setMode("booking")
            return confirmResponse?.data?.data?.bookingReference;
          } else {
            setbookingError("Booking failed at this moment. Please try later or contact support.");
            throw new Error(
                confirmResponse?.error ||
                confirmResponse?.error?.message ||
                confirmResponse?.data?.error?.error_message ||
                "Error in Confirmation booking"
            );
          }
        }
      }
    } catch (error) {
      if(hotelDetails?.vendor === 'RATEHAWK'){
        const response = await ApiCall("POST", "/hotels/booking-status", { id: hotelDetails?._id, vendor: hotelDetails?.vendor });
        if (response?.data?.data?.bookingReference && response?.data?.data?.bookingId) {
          navigate(`/hotels/checkout/${response?.data?.data?.bookingReference}?bookingId=${response?.data?.data?.bookingId}&type=${toUrlVendor(hotelDetails?.vendor)}`)
          return response?.data?.data?.bookingReference;
        }
      }
      // await paymentCancelCommonFn(paymentMethod?.id,userToken);
      // await credit(totalPriceForOnline,"Hotel Booking Error", "online",transactionId);
      setError(error?.message);
      return null;
    } finally {
      setLoading(false);
      payloadRef.current = null
    }
  };

  const doTransaction = async (amount, payload, hotelDetails,commonId) => {
    try {
      const walletBalance = await getBalance();
      setLoading(true);
      setError(null);
      const roundedAmount = parseFloat(amount) || 0
      if (!isNaN(roundedAmount) && parseFloat(walletBalance) >= parseFloat(roundedAmount)) {
        const deductBalanceFromWallet = await debit(roundedAmount, `Debited Towards: Hotel Booking ${hotelDetails?._id}`, 'wallet');
        if (
          deductBalanceFromWallet?.status === 200 &&
          deductBalanceFromWallet?.data?.success &&
          deductBalanceFromWallet?.data?.transaction_id
        ) {
          const reqPayload = preparePayload(payload, hotelDetails,'wallet',0,deductBalanceFromWallet?.data?.transaction_id,commonId,agentActiveCurrency);
          const confirmResponse = await confirmBooking({...reqPayload,commonId}, roundedAmount);
          if (confirmResponse?.data?.data?.bookingReference && confirmResponse?.data?.data?.bookingStatus === 'RR' || confirmResponse?.data?.data?.bookingStatus === 'CONFIRMED') {
            setbookingError("")
            window.scrollTo(0, 0);
            return confirmResponse?.data?.bookingReference;
          } else if (confirmResponse?.data?.data?.bookingReference) {
            setbookingError("Booking failed this moment , Please try again or contact support.")
            throw new Error(
              confirmResponse?.error ||
              confirmResponse?.error?.message ||
              confirmResponse?.data?.error?.error_message ||
              "Error in Confirmation booking"
            );
          } else {
            setbookingError("Booking failed at this moment. Please try later or contact support.")
            throw new Error(
              confirmResponse?.error ||
              confirmResponse?.error?.message ||
              confirmResponse?.data?.error?.error_message ||
              "Error in Confirmation booking"
            );
          }
        }
        return null;
      } else {
        setError(`Insufficient Balance. Your Total Balance is: ${walletBalance} &nbsp;
        <a target="_blank" href="${process.env.wordpressApiEndPoint}dashboard/?tab=new-balance-request" class="hw-link--line">Add your Balance</a>`);
      }
    } catch (error) {
      if(hotelDetails?.vendor === 'RATEHAWK') {
        const response = await ApiCall("POST", "/hotels/booking-status", {id : hotelDetails?._id , vendor : hotelDetails?.vendor });
        if(response?.data?.data?.bookingReference && response?.data?.data?.bookingId){
          navigate(`/hotels/checkout/${response?.data?.data?.bookingReference}?bookingId=${response?.data?.data?.bookingId}&type=${toUrlVendor(hotelDetails?.vendor)}`)
          return response?.data?.data?.bookingReference;
        }
      }
      await credit(parseFloat(amount) || 0, "Credited Towards: Hotel Booking Error", payload.name);
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const doOnlinePayment = (payload, hotelDetails , onineServicesFees) => {
    try {
      const reqPayload = preparePayload(payload, hotelDetails, "online" , onineServicesFees);
      payloadRef.current = reqPayload;
      setLoading(true);
      setError(null);
      setbookingError("");
      addListner();
      setTimeout(() => {
        const onlineBtn = document.querySelector(".online_book_now_btn");
        if (onlineBtn) {
          onlineBtn.click();
        } else {
          console.error("Online payment button not found");
          setLoading(false);
        }
      }, 500);
    } catch (error) {
      console.error("Error in doOnlinePayment:", error);
      setLoading(false);
      setError("Failed to initiate online payment. Please try again.");
    }
  };


  return {
    getBalance,
    doTransaction,
    loading: loading || walletLoading || onlinePaymentLoading,
    error: error || onlinePaymentError || walletPaymentError,
    doOnlinePayment,
    confirmAfterOnlinePayment,
    setLoading,
    userToken,
    bookingError
  };
};
